/**
* @license Apache-2.0
*
* Copyright (c) 2023 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isndarrayLike from '@stdlib/assert-is-ndarray-like';
import isMultiSlice from '@stdlib/assert-is-multi-slice';
import isArrayLikeObject from '@stdlib/assert-is-array-like-object';
import isPlainObject from '@stdlib/assert-is-plain-object';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import hasOwnProp from '@stdlib/assert-has-own-property';
import MultiSlice from '@stdlib/slice-multi';
import args2multislice from '@stdlib/slice-base-args2multislice';
import base from '@stdlib/ndarray-base-slice';
import getShape from '@stdlib/ndarray-shape';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Returns a read-only view of an input ndarray.
*
* @param {ndarray} x - input array
* @param {...*} s - slice arguments
* @param {Options} [options] - options
* @param {boolean} [options.strict] - boolean indicating whether to enforce strict bounds checking
* @throws {TypeError} first argument must be an ndarray
* @throws {TypeError} must provide valid slice arguments
* @throws {Error} insufficient arguments
* @throws {Error} too many arguments
* @throws {TypeError} options argument must be an object
* @throws {TypeError} must provide valid options
* @throws {RangeError} number of slice dimensions must match the number of array dimensions
* @throws {RangeError} slice exceeds array bounds
* @returns {ndarray} ndarray view
*
* @example
* import Slice from '@stdlib/slice-ctor';
* import MultiSlice from '@stdlib/slice-multi';
* import getShape from '@stdlib/ndarray-shape';
* import ndarray from '@stdlib/ndarray-ctor';
*
* var buffer = [ 1.0, 2.0, 3.0, 4.0, 5.0, 6.0 ];
* var shape = [ 3, 2 ];
* var strides = [ 2, 1 ];
* var offset = 0;
*
* var x = ndarray( 'generic', buffer, shape, strides, offset, 'row-major' );
* // returns <ndarray>[ [ 1.0, 2.0 ], [ 3.0, 4.0 ], [ 5.0, 6.0 ] ]
*
* var sh = getShape( x );
* // returns [ 3, 2 ]
*
* var s = new MultiSlice( new Slice( null, null, -2 ), new Slice( null, null, -1 ) );
* // returns <MultiSlice>
*
* var y = slice( x, s );
* // returns <ndarray>[ [ 6.0, 5.0 ], [ 2.0, 1.0 ] ]
*
* sh = getShape( y );
* // returns [ 2, 2 ]
*/
function slice( x, s ) {
	var options;
	var nargs;
	var opts;
	var args;
	var sh;
	var S;
	var i;

	opts = {
		'strict': true
	};
	nargs = arguments.length;
	if ( !isndarrayLike( x ) ) {
		throw new TypeError( format( '1jV4f', x ) );
	}
	if ( isPlainObject( arguments[ nargs-1 ] ) ) {
		nargs -= 1;
		options = arguments[ nargs ];
		if ( hasOwnProp( options, 'strict' ) ) {
			if ( !isBoolean( options.strict ) ) {
				throw new TypeError( format( '1jV2o', 'strict', options.strict ) );
			}
			opts.strict = options.strict;
		}
		sh = getShape( x );
		if ( nargs === 1 && sh.length > 0 ) {
			throw new RangeError( format( '1jVEt', sh.join( ',' ), 0 ) );
		}
	}
	if ( isMultiSlice( s ) ) {
		S = s;
		if ( nargs > 2 ) {
			throw new Error( format('1jV0m') );
		}
	} else {
		if ( isArrayLikeObject( s ) ) {
			args = s;
			if ( nargs > 2 ) {
				throw new Error( format('1jV0m') );
			}
		} else {
			args = [];
			for ( i = 1; i < nargs; i++ ) {
				args.push( arguments[ i ] );
			}
		}
		try {
			S = args2multislice( args );
		} catch ( err ) { // eslint-disable-line no-unused-vars
			// Search for the first offending value...
			for ( i = 0; i < args.length; i++ ) {
				try {
					new MultiSlice( args[ i ] ); // eslint-disable-line no-new
				} catch ( err ) { // eslint-disable-line no-unused-vars
					throw new TypeError( format( '1jVEu', String( args[ i ] ) ) );
				}
			}
		}
	}
	return base( x, S, opts.strict, false );
}


// EXPORTS //

export default slice;
